import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Home, Lock } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import type { ListProps } from '@mui/material';
import type { ActionType, URLParamsType } from '../../../types';
import type { RootState } from '../../../reducers';
import { getFolderUrl } from '../../../lib/routingUtils';
import FreeTrial from '../../plan/upgrade/FreeTrial';
import { useUserTags } from '../../../hooks/useTags';
import { setDrawerState } from '../dashboardSlice';
import SearchComponent from './Search';
import EventsComponent from './Events';
import MultiAskAIComponent from './MultiAskAI';
import HistoryButton from './HistoryButton';

interface DrawerActionType extends ActionType<DrawerActionKeyType> {
  tooltip: string;
}

type DrawerActionKeyType = 'recents' | 'private';

const DrawerActions = (listProps: ListProps) => {
  const [showTrialModal, setShowTrialModal] = useState(false);
  const { t } = useTranslation();
  const { dashboard, team } = useSelector((state: RootState) => state);
  const { setTag, getTag } = useUserTags();
  const { folderId } = useParams<URLParamsType>();
  const { selectedTeam, teams } = team;
  const teamId = selectedTeam?.teamId || (teams[0]?.teamId as string);
  const open = dashboard.drawerState === 'open';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const showHistoryAtTop = getTag('feature_history_btn_top') === 'true';

  const drawerActions: DrawerActionType[] = [
    {
      key: 'recents',
      label: t('drawer.quickActions.home.label'),
      startIcon: <Home />,
      tooltip: t('drawer.quickActions.home.tooltip'),
    },
    {
      key: 'private',
      label: t('drawer.quickActions.private.label'),
      startIcon: <Lock />,
      tooltip: t('drawer.quickActions.private.tooltip'),
    },
  ];

  const handleQuickActionClick = () => {
    // Set default view and sorting preferences using tags
    setTag({ key: 'viewMode', value: 'grid' });
    setTag({ key: 'sortBy', value: 'createdAt' });
    setTag({ key: 'sortOrder', value: 'desc' });

    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  const actions: DrawerActionType[] = drawerActions.map((action) => {
    return {
      ...action,
      href: getFolderUrl({
        teamId,
        folderId: action.key,
        withBasePath: false,
      }),
    };
  });

  return (
    <>
      <List
        {...listProps}
        sx={{
          display: 'block',
          pt: 0.5,
          pb: 0,
          '& .MuiListItem-root:not(:last-child)': {
            mb: 0.5,
          },
          mb: 0,
        }}
      >
        {actions.map(({ key, label, startIcon, href, tooltip }) => (
          <ListItem key={key} disablePadding sx={{ display: 'block' }}>
            <Tooltip title={open ? tooltip : label} placement="right" arrow>
              <ListItemButton
                component={Link}
                to={href || ''}
                onClick={handleQuickActionClick}
                sx={{
                  justifyContent: open ? 'initial' : 'center',
                  px: open ? 2.5 : 1.25,
                  borderRadius: 20,
                  mx: 0.5,
                  minHeight: '36px',
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: 'transparent',
                  '&.Mui-selected': {
                    bgcolor: 'transparent',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    backgroundColor:
                      folderId === key
                        ? theme.palette.action.hover
                        : theme.palette.action.hover,
                    opacity: folderId === key ? 1 : 0,
                    borderRadius: 'inherit',
                  },
                  '&:hover': {
                    '&::before': {
                      opacity: 1,
                    },
                  },
                  '& > *': {
                    position: 'relative',
                    zIndex: 1,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    pl: open ? 0 : 0.5,
                    color: folderId === key ? 'inherit' : 'text.secondary',
                    transition: theme.transitions.create(
                      ['margin', 'padding', 'color'],
                      {
                        duration: 150,
                        easing: theme.transitions.easing.easeOut,
                      }
                    ),
                    '& svg': {
                      color: 'inherit',
                    },
                  }}
                >
                  {startIcon}
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  sx={{
                    opacity: open ? 1 : 0,
                    transition: theme.transitions.create(['opacity'], {
                      duration: open ? 150 : 100,
                      easing: theme.transitions.easing.easeOut,
                      delay: open ? 50 : 0,
                    }),
                    '& .MuiTypography-root': {
                      fontWeight: folderId === key ? 700 : 400,
                      color:
                        folderId === key ? 'text.primary' : 'text.secondary',
                    },
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}

        <MultiAskAIComponent open={open} />
        <SearchComponent open={open} />
        <EventsComponent open={open} />

        {/* Conditionally show History button at top if feature flag is enabled */}
        {showHistoryAtTop && <HistoryButton open={open} />}
      </List>

      {showTrialModal && <FreeTrial onClose={() => setShowTrialModal(false)} />}
    </>
  );
};

export default DrawerActions;
